import { supabase } from '../lib/supabase';

/**
 * Check if a student has already unlocked a specific trail
 * @param {string} studentId
 * @param {string} trailId
 * @returns {Promise<{unlocked: boolean, error: null}>}
 */
export const isTrailUnlocked = async (studentId, trailId) => {
  try {
    const { data, error } = await supabase
      ?.from('student_unlocked_trails')
      ?.select('id')
      ?.eq('student_id', studentId)
      ?.eq('trail_id', trailId)
      ?.maybeSingle();

    if (error) {
      console.error('isTrailUnlocked error:', error);
      return { unlocked: false, error: error?.message };
    }

    return { unlocked: !!data, error: null };
  } catch (err) {
    console.error('isTrailUnlocked exception:', err);
    return { unlocked: false, error: 'Failed to check trail unlock status' };
  }
};

/**
 * Unlock a trail for a student (insert record if not already unlocked)
 * @param {string} studentId
 * @param {string} trailId
 * @returns {Promise<{success: boolean, alreadyUnlocked: boolean, error: null}>}
 */
export const unlockTrail = async (studentId, trailId) => {
  try {
    const { data, error } = await supabase
      ?.from('student_unlocked_trails')
      ?.insert([{ student_id: studentId, trail_id: trailId }])
      ?.select()
      ?.single();

    if (error) {
      // Unique constraint violation means already unlocked
      if (error?.code === '23505') {
        return { success: true, alreadyUnlocked: true, error: null };
      }
      console.error('unlockTrail error:', error);
      return { success: false, alreadyUnlocked: false, error: error?.message };
    }

    return { success: true, alreadyUnlocked: false, error: null };
  } catch (err) {
    console.error('unlockTrail exception:', err);
    return { success: false, alreadyUnlocked: false, error: 'Failed to unlock trail' };
  }
};

/**
 * Get all unlocked trails for a student with trail details and progress
 * @param {string} studentId
 * @returns {Promise<{trails: Array, error: null}>}
 */
export const getUnlockedTrails = async (studentId) => {
  try {
    if (!studentId) return { trails: [], error: null };

    // Get all unlocked trail IDs for this student
    const { data: unlocked, error: unlockError } = await supabase
      ?.from('student_unlocked_trails')
      ?.select('trail_id, unlocked_at')
      ?.eq('student_id', studentId)
      ?.order('unlocked_at', { ascending: false });

    if (unlockError) {
      console.error('getUnlockedTrails unlock query error:', unlockError);
      return { trails: null, error: unlockError?.message };
    }

    if (!unlocked || unlocked?.length === 0) {
      return { trails: [], error: null };
    }

    const trailIds = unlocked?.map(u => u?.trail_id);

    // Fetch trail details with student progress
    const { data: trails, error: trailsError } = await supabase
      ?.from('learning_trails')
      ?.select(`
        *,
        student_progress!left(
          id,
          student_id,
          started_at,
          completed_at,
          completion_percentage,
          answers_data,
          last_accessed_at,
          stars_earned
        )
      `)
      ?.in('id', trailIds)
      ?.eq('student_progress.student_id', studentId);

    if (trailsError) {
      console.error('getUnlockedTrails trails query error:', trailsError);
      return { trails: null, error: trailsError?.message };
    }

    // Merge unlocked_at into trail data and sort by unlock order
    const unlockedMap = {};
    unlocked?.forEach(u => { unlockedMap[u?.trail_id] = u?.unlocked_at; });

    const transformedTrails = trails?.map(trail => ({
      id: trail?.id,
      title: trail?.title,
      description: trail?.description,
      imageUrl: trail?.image_url,
      qrCodeUrl: trail?.qr_code_url,
      published: trail?.published,
      createdAt: trail?.created_at,
      unlockedAt: unlockedMap?.[trail?.id],
      studentProgress: trail?.student_progress?.[0] ? {
        id: trail?.student_progress?.[0]?.id,
        studentId: trail?.student_progress?.[0]?.student_id,
        startedAt: trail?.student_progress?.[0]?.started_at,
        completedAt: trail?.student_progress?.[0]?.completed_at,
        completionPercentage: trail?.student_progress?.[0]?.completion_percentage,
        answersData: trail?.student_progress?.[0]?.answers_data,
        lastAccessedAt: trail?.student_progress?.[0]?.last_accessed_at,
        starsEarned: trail?.student_progress?.[0]?.stars_earned
      } : null
    }));

    // Sort by unlocked_at descending (most recently unlocked first)
    transformedTrails?.sort((a, b) => new Date(b?.unlockedAt) - new Date(a?.unlockedAt));

    return { trails: transformedTrails, error: null };
  } catch (err) {
    console.error('getUnlockedTrails exception:', err);
    return { trails: null, error: 'Failed to fetch unlocked trails' };
  }
};
